<template>
  <!-- Container -->
  <div class="w-full max-w-full filterAnimation px-5" style="z-index: 5000000">
      <!-- New ads refresh button -->
      <NewAdvertisementButton v-if="getNewAds.length" @click="$emit('refresh')" />

    <!-- Main Bar -->
    <div class="flex items-center">
      <!-- Search Section -->
      <div v-if="noSearch" class="h-12"/>
      <div v-else class="search-input-container group py-1.5 px-2 my-2 flex flex-grow items-center transition rounded-md"
      style="min-width: 200px; max-width: 250px">
        <!-- Search Image -->
        <div class="w-5 h-5">
          <SearchIcon class="transition-colors ease-in-out"
          :class="searchFocused ? 'text-icon-normal' : 'text-text-subdued group-hover:text-icon-muted'"/>
        </div>
        <!-- Text Input -->
        <input ref="searchInput" :value="textSearch" placeholder="Search..." 
        @input="emitUpdate({ emitString: 'update:textSearch', items: $event.target.value, })"
        @focus="searchFocused = true" @blur="searchFocused = false"
        class="ml-1 text-sm rounded flex items-center outline-none transition-all border-none w-full 
        focus:border-none text-gray-500 bg-white bg-opacity-0">
      </div>
      <div v-if="!noSearch" class="flex flex-col mx-3">
        <div class="h-6 my-auto w-0 border-r border-border-normal" />
      </div>
      <!-- Filters -->
      <div class="h-full flex flex-grow justify-between items-center">
        <div class="relative" v-on-clickaway="() => { if (!showFilterPresetModal) filterDropdownExpanded = false }">
          <!-- Add Filter Button -->
          <button class="flex items-center p-1.5 rounded-md transition-colors duration-100 flex-shrink-0 whitespace-nowrap"
          :class="[filterDropdownExpanded ? 'bg-neutral-25' : 'hover:bg-neutral-25', {'cursor-not-allowed': !getFilterCategories.length || !getIsSubscriptionActive}]"
          :disabled="!getFilterCategories.length || !getIsSubscriptionActive"
          @click="filterDropdownExpanded = !filterDropdownExpanded">
            <FilterIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="px-1.5 text-text-muted">
              Add Filter
            </BaseText>
          </button>
          <!-- Filter Select Dropdown -->
          <transition>
            <div v-if="getFilterCategories.length && filterDropdownExpanded" class="filter-select-dropdown">
              <FilterSelectDropdown
                :active-filters="activeFilters"
                :allowed-filter-types="allowedFilterTypes"
                :excluded-filter-types="excludedFilterTypes"
                :hide-saved-filters="hideSavedFilters"
                @applyFilters="applyFilterChanges($event)"
                @editPreset="editPreset($event)"
                @createPreset="createPreset"
                @close="filterDropdownExpanded = false"
              />
            </div>
          </transition>
        </div>
        <!-- Active Filters List -->
        <div class="pl-2 flex-grow">
          <ActiveFiltersList 
            :active-filters="activeFilters" 
            :hide-save-option="hideSavedFilters || !!(allowedFilterTypes.length) || !!(excludedFilterTypes.length)"
            @applyFilters="applyFilterChanges($event)"
            @createPreset="createPreset(activeFilters)"
          />
        </div>

        <!-- Right Side -->
        <div class="flex items-center gap-1.5 justify-end h-full" :class="noSearch ? '' : 'ml-auto'">

          <!-- Show only Favorite (brands) -->
          <div v-if="typeof favoriteFilter === 'boolean'"
          class="dropdown-button flex items-center gap-0.5 pl-2 pr-2.5 rounded-md cursor-pointer" 
          :class="favoriteFilter ? 'expanded py-1' : 'py-1.5'"
          @click="() => emitUpdate({ emitString: 'update:favoriteFilter', items: !favoriteFilter })">
            <FavoriteIcon :active="favoriteFilter" noHover class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted no-select">
              Favorites
            </BaseText>
          </div>

          <!-- Date Range Selector -->
          <div v-if="selectedDateRange" class="relative" 
          v-on-clickaway="() => { if (selectorExpanded.date) setSelectorExpanded('date', false) }">
            <div class="dropdown-button flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md cursor-pointer"
            @click="getIsSubscriptionActive ? setSelectorExpanded('date', !selectorExpanded.date) : null" :class="{'expanded': selectorExpanded.date, 'cursor-not-allowed': !getIsSubscriptionActive}">
              <CalendarIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted no-select">
                {{ dateFilterLabel }}
              </BaseText>
            </div>
            <transition>
              <div v-if="selectorExpanded.date" class="date-selector-dropdown">
                <DateRangeSelectorDropdown 
                  :selectedDateRange="getSelectedDateRange"
                  :dateFilterLabel.sync="dateFilterLabel"
                  @update:selectedDateRange="handleDateChange"
                  @close="setSelectorExpanded('date', false)"
                />
              </div>
            </transition>
          </div>

          <!-- Sort Order Dropdown Selector -->
          <SortDropdown 
            v-if="$route.name !== 'DiscoveryBrandsView'"
            :selected-sort="sortOrder[0]?.value || 'desc'"
            :sort-categories="sortCategories"
            emit-full-option
            @select-sort="emitUpdate({ emitString: 'update:sortOrder', items: [$event] })"
          />

          <!-- Unique Creatives Dropdown Selector -->
          <div v-if="$route.name !== 'LibraryView' && $route.name !== 'BoardsView' && !discoveryBrands" class="relative" v-on-clickaway="() => { if (selectorExpanded.uniqueCreatives) setSelectorExpanded('uniqueCreatives', false) }">
            <div class="dropdown-button flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md cursor-pointer"
            @click="setSelectorExpanded('uniqueCreatives', !selectorExpanded.uniqueCreatives)" :class="{'expanded': selectorExpanded.uniqueCreatives}">
              <SameCreativeIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted no-select">
                {{ localUniqueCreatives ? 'On' : 'Off' }}
              </BaseText>
            </div>
            <transition>
              <div v-if="selectorExpanded.uniqueCreatives" class="same-creative-dropdown flex flex-col gap-0.5 p-1 rounded-lg bg-white" style="width: 240px;">
                <div class="dropdown-item group" :class="{'selected': localUniqueCreatives}" @click="handleUniqueCreativesToggle()">
                  <SameCreativeIcon class="text-icon-normal" />
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Group Duplicates
                  </BaseText>
                  <div class="ml-auto">
                    <BaseToggle :value="localUniqueCreatives" @input="updateUniqueCreatives($event)" :color="`primary-${getTheme}-100`" />
                  </div>
                </div>
                <!-- Sub Text -->
                <div class="flex bg-background-normal rounded-md items-center gap-1 px-2 py-1">
                  <BaseText type="body" size="xs" class="text-text-muted">
                    Group together Meta ads that use the same image or video
                  </BaseText>
                </div>
              </div>
            </transition>
          </div>

          <!-- Visible Dropdown Selector -->
          <div v-if="!(typeof favoriteFilter === 'boolean') && !hideAdSettings" class="relative"
          v-on-clickaway="() => { if (selectorExpanded.visible) setSelectorExpanded('visible', false) }">
            <div class="dropdown-button flex pl-2 pr-1 py-1.5 rounded-md cursor-pointer"
            @click="setSelectorExpanded('visible', !selectorExpanded.visible)" :class="{'expanded': selectorExpanded.visible}">
              <SetVisibleIcon class="text-icon-normal" />
              <div class="transform transition-transform duration-200" :style="selectorExpanded.visible ? 'transform: scale(1, -1)' : ''">
                <ChevronIcon />
              </div>
            </div>
            <transition>
              <div v-if="selectorExpanded.visible" class="visible-selector-dropdown flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                <div v-for="(setting, index) in ['Brand Details', 'Ad Copy', 'Headline & CTA']" :key="`visible-setting-${index}`"
                class="dropdown-item group" @click="toggleVisibleSetting(setting)">
                  <BaseText type="body" size="sm" class="text-text-normal transition-colors group-hover:text-black">
                    {{ setting }}
                  </BaseText>
                  <div class="ml-auto">
                    <BaseToggle :value="adSettings.some(s => s.value === setting)" :color="`primary-${getTheme}-100`" />
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- View Dropdown Selector -->
          <div v-if="selectedView" class="relative" v-on-clickaway="() => { if (selectorExpanded.view) setSelectorExpanded('view', false) }">
            <div class="dropdown-button flex pl-2 pr-1 py-1.5 rounded-md cursor-pointer"
            @click="setSelectorExpanded('view', !selectorExpanded.view)" :class="{'expanded': selectorExpanded.view}">
              <GridViewIcon v-if="selectedView === 'grid'" class="text-icon-normal" />
              <TableViewIcon v-else-if="selectedView === 'table'" class="text-icon-normal" />
              <GanttViewIcon v-else class="text-icon-normal" />
              <div class="transform transition-transform duration-200" :style="selectorExpanded.view ? 'transform: scale(1, -1)' : ''">
                <ChevronIcon />
              </div>
            </div>
            <transition>
              <div v-if="selectorExpanded.view" class="view-selector-dropdown flex flex-col gap-0.5 p-1 rounded-lg bg-white no-select"
              :style="{width: viewOptions.some((view) => view.value === 'gantt') ? '175px' : '156px'}">
                <div class="dropdown-item group" :class="{'selected': selectedView === 'grid'}"
                @click="setSelectorExpanded('view', false), $emit('update:selectedView', 'grid')">
                  <GridViewIcon class="text-icon-normal" />
                  <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-black">
                    Grid View
                  </BaseText>
                  <div v-if="selectedView === 'grid'" class="ml-auto text-icon-normal">
                    <NewCheckmarkIcon />
                  </div>
                </div>
                <div v-if="viewOptions.some((view) => view.value === 'table')" class="dropdown-item group" :class="{'selected': selectedView === 'table'}"
                @click="setSelectorExpanded('view', false), $emit('update:selectedView', 'table')">
                  <TableViewIcon class="text-icon-normal" />
                  <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-black">
                    List View
                  </BaseText>
                  <div v-if="selectedView === 'table'" class="ml-auto text-icon-normal">
                    <NewCheckmarkIcon />
                  </div>
                </div>
                <div v-if="viewOptions.some((view) => view.value === 'gantt')" class="dropdown-item group" :class="{'selected': selectedView === 'gantt'}"
                @click="setSelectorExpanded('view', false), $emit('update:selectedView', 'gantt')">
                  <GanttViewIcon class="text-icon-normal" />
                  <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-black">
                    Timeline View
                  </BaseText>
                  <div v-if="selectedView === 'gantt'" class="ml-auto text-icon-normal">
                    <NewCheckmarkIcon />
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <portal to="folder-modals">
      <FilterPresetModal 
        v-if="showFilterPresetModal"
        :initFilterPreset="editingPreset"
        :initFilters="newPresetFilters"
        :allowed-filter-types="allowedFilterTypes"
        :excluded-filter-types="excludedFilterTypes"
        @close="showFilterPresetModal = false"
      />
    </portal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import firebase from '../../api/config/FirebaseConfig'
import FavoriteIcon from '../globals/Icons/FavoriteIcon.vue'
import TagIcon from '../globals/Icons/TagIcon.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import NewCheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import SortIcon from '../globals/Icons/SortIcon.vue'
import CalendarIcon from '../globals/Icons/CalendarIcon.vue'
import SetVisibleIcon from '../globals/Icons/SetVisibleIcon.vue'
import * as ViewIcons from '../globals/Icons/ViewTypeIcons'
import DateRangeSelectorDropdown from './TopBarComponents/DateRangeSelectorDropdown.vue'
import NewAdvertisementButton from '../../components/advertisements/NewAdvertisementButton.vue'
import SwipeFileBrandsIcon from '../globals/Icons/SwipeFileIcons/SwipeFileBrandsIcon.vue'
import AdsCarouselIcon from '../globals/Icons/AdsCarouselIcon.vue'

// Filter Components
import FilterSelectDropdown from '../filters/FilterSelectDropdown.vue'
import SortDropdown from './TopBarComponents/SortDropdown.vue'
import ActiveFiltersList from '../filters/ActiveFiltersList.vue'
import FilterPresetModal from '../filters/FilterPresetModal.vue'
import FilterIcon from '../globals/Icons/FilterIcons/FilterIcon.vue'
import SameCreativeIcon from '../globals/Icons/SameCreativeIcon.vue'

export default {
  name: 'FilterTopBar',
  mixins: [clickaway],
  components: {
    DateRangeSelectorDropdown,
    FavoriteIcon,
    TagIcon,
    SearchIcon,
    ChevronIcon,
    NewAdvertisementButton,
    NewCheckmarkIcon,
    SortIcon,
    CalendarIcon,
    SetVisibleIcon,
    ...ViewIcons,

    // Filters
    FilterSelectDropdown,
    SortDropdown,
    FilterPresetModal,
    FilterIcon,
    ActiveFiltersList,
    SameCreativeIcon
  },
  props: {
    keywordSearchOpened: {
      type: Boolean,
      default: false
    },
    activeFilters: {
      type: Object,
      default: () => ({})
    },
    // If empty, all filter types are allowed
    allowedFilterTypes: {
      type: Array,
      default: () => []
    },
    excludedFilterTypes: {
      type: Array,
      default: () => []
    },
    textSearch: {
      type: String,
      default: () => ''
    },
    favoriteFilter: {
      type: Boolean,
      default: () => null
    },
    noSearch: {
      type: Boolean,
      default: () => false
    },
    uniqueCreatives: {
      type: Boolean,
      default: () => true
    },
    discoveryBrands: {
      type: Boolean,
      default: () => false
    },
    alphabetical: {
      type: Boolean,
      default: () => false
    },
    sortOrder: {
      type: Array,
      default: () => []
    },
    hideAdSettings: {
      type: Boolean,
      default: () => false
    },
    csvExport: {
      type: Boolean,
      default: () => false
    },
    externalFilters: {
      type: Array,
      default: () => []
    },
    selectedView: {
      type: String,
      default: () => null
    },
    viewOptions: {
      type: Array,
      default: () => [
        { name: 'Grid', value: 'grid' },
        { name: 'Table', value: 'table' }
      ]
    },
    selectedDateRange: {
      type: Object,
      default: () => {}
    },
    hideSavedFilters: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      focused: null,
      timer: null,
      facebookImage: require('../../assets/images/facebook-library.png'),
      instagramImage: require('../../assets/images/instagram-small.png'),
      tiktokImage: require('../../assets/images/tiktok-organic.png'),
      adSettings: [],
      loadingSave: false,
      currentUser: {},
      additionalFilters: 0,
      searchFocused: false,
      dateFilterLabel: 'All Time',
      sortLabel: 'Newest',
      localUniqueCreatives: this.uniqueCreatives,
      selectorExpanded: {
        view: false,
        sort: false,
        visible: false,
        date: false,
        uniqueCreatives: false
      },

      // NEW STATE (SORT LATER)
      filterDropdownExpanded: false,
      showFilterPresetModal: false,
      editingPreset: null,
      newPresetFilters: {}
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getBoards']),
    ...mapGetters('AuthModule', ['getUser', 'getTeam']),
    ...mapGetters('MiscModule', ['getTheme']),
    ...mapGetters('AdvertisementsModule', ['getNewAds']),
    ...mapGetters('SearchModule', ['getFilterCategories']),
    ...mapGetters('AuthModule', ['getIsSubscriptionActive']),
    getSelectedDateRange () {
      this.changeAdditionalFilters()
      return this.selectedDateRange
    },
    sortCategories () {
      const alphabeticalBrandSort = [
        {
          label: 'Brands',
          icon: SwipeFileBrandsIcon,
          options: [
            ...(this.discoveryBrands ? [{ value: 'saved', name: 'Ads Saved' }] : []),
            { value: 'asc', name: 'A-Z' },
            { value: 'desc', name: 'Z-A' }
          ]
        }
      ]
      const adsSort = [
        {
          label: 'Ads',
          icon: AdsCarouselIcon,
          options: [
            ...(this.textSearch?.length ? [{ value: 'relevance', name: 'Most Relevant' }] : []),
            { value: 'desc', name: 'Newest' },
            { value: 'asc', name: 'Oldest' },
            { value: 'longest', name: 'Longest Running' },
          ]
        }
      ] 
      return this.alphabetical ? alphabeticalBrandSort : adsSort
    },
  },
  watch: {
    'selectedDateRange.start': function () {
      this.changeAdditionalFilters()
    },
    'selectedDateRange.end': function () {
      this.changeAdditionalFilters()
    },
    keywordSearchOpened (newVal) {
      this.searchFocused = newVal
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
          this.$emit('resetKeywordSearch')
        })
      }
    },
    filterDropdownExpanded (newVal) {
      this.$emit('toggleScrollLock', newVal)
    },
    uniqueCreatives (newValue) {
      this.localUniqueCreatives = newValue
    }
  },
  mounted () {
    this.currentUser = {
      ...this.getUser,
      first_name: this.getUser.first_name || this.getUser.name
    }
    this.adSettings = this.currentUser.adSettings || [
      { name: 'Brand Details', value: 'Brand Details' }
    ]
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER']),
    ...mapMutations('SearchModule', ['SET_CURRENT_TEXT_SEARCH', 'SET_CURRENT_FILTERS']),
    applyFilterChanges (filters) {
      this.$emit('update:activeFilters', { ...filters })
      this.$emit('change')
    },
    handleDateChange (date) {
      this.$emit('update:selectedDateRange', date)
      this.$emit('change')
    },
    changeAdditionalFilters () {
      this.additionalFilters = (this.selectedDateRange?.start === null && this.selectedDateRange?.end === null) ? 0 : 1
    },
    createPreset (initialFilters = {}) {
      this.showFilterPresetModal = true
      this.newPresetFilters = initialFilters
      this.editingPreset = null
    },
    editPreset (preset) {
      this.showFilterPresetModal = true
      this.editingPreset = preset
    },
    unFocus (toUnfocus) {
      if (toUnfocus === this.focused) {
        this.focused = null
        this.$emit('resetKeywordSearch')
      }
    },
    setSelectorExpanded (dropdown, isExpanded) {
      // Toggle the specified dropdown, close all others
      this.selectorExpanded[dropdown] = isExpanded
      Object.keys(this.selectorExpanded).forEach((key) => {
        if (key !== dropdown) {
          this.selectorExpanded[key] = false
        }
      })
    },
    toggleVisibleSetting (setting) {
      if (this.adSettings.some((s) => s.value === setting)) {
        this.adSettings = this.adSettings.filter((s) => s.value !== setting)
      } else {
        this.adSettings = [...this.adSettings, { name: setting, value: setting }]
      }
      this.updateAdSettings(this.adSettings)
    },
    handleUniqueCreativesToggle () {
      this.setSelectorExpanded('uniqueCreatives', false)
      this.updateUniqueCreatives(!this.localUniqueCreatives)
    },
    updateUniqueCreatives (value) {
      this.localUniqueCreatives = value
      this.$emit('update:uniqueCreatives', value)
      this.$emit('change')
    },
    async updateAdSettings (settings) {
      try {
        const db = firebase.firestore()
        delete this.currentUser.token
        this.currentUser.adSettings = settings

        await db.collection('auth-users').doc(this.currentUser.user_id).update({
          adSettings: settings
        })
        this.SET_USER({
          ...this.getUser,
          ...this.currentUser,
          token: this.getUser.uid
        })
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 100)
      } catch (err) {
        console.log(err)
      } finally {
        this.loadingSave = false
      }
    },
    emitUpdate (emitObject) {
      const { emitString, items } = emitObject

      if (emitString === 'update:textSearch') {
        if (this.timer) {
          clearTimeout(this.timer)
          this.timer = null
        }

        this.timer = setTimeout(async () => {
          if (this.textSearch === '') {
            this.$emit('update:sortOrder', [
              {
                name: 'Most Relevant',
                value: 'relevance',
                disabled: false
              }
            ])
          } else if (items === '') {
            this.$emit('update:sortOrder', [{ name: 'Newest', value: 'desc' }])
          }
          // Track event if user searched
          window.analytics.track('User Searched Ai', {
            searchQuery: this.textSearch
          })

          this.$emit(emitString, items)
          this.$emit('change')
        }, 1000)
      } else {
        console.log('Emitting the following event: ', emitString, items)
        this.$emit(emitString, items)
        this.$emit('change')
      }
    },
    clearAllFilters () {
      this.$emit('update:activeFilters', {})
      this.$emit('update:selectedDateRange', { start: null, end: null })
    }
  }
}
</script>

<style scoped lang="css">
  .filterAnimation {
    animation: fade 500ms ease-in-out;
  }
  .search-input-container {
    --placeholder-color: #5E6678;
    transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
    background-color: transparent;
    box-shadow: none;
  }
  .search-input-container:hover {
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    background-color: white;
    --placeholder-color: black;
  }
  .search-input-container:focus-within {
    box-shadow: none;
    background-color: transparent;
    --placeholder-color: #A4ACB9;
  }
  .search-input-container input::placeholder {
    transition: color 150ms ease-in-out;
    color: var(--placeholder-color);
  }
  .dropdown-button {
    transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
    background-color: transparent;
    box-shadow: none;
    white-space: nowrap;
  }
  .dropdown-button:hover {
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    background-color: white;
  }
  .dropdown-button.expanded {
    box-shadow: none;
    background-color: #F6F8FA;
  }
  .visible-selector-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 195px;
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    z-index: 500;
  }
  .view-selector-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    z-index: 500;
  }
  .same-creative-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 195px;
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    z-index: 500;
  }
  .sort-selector-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 170px;
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    z-index: 500;
  }
  .date-selector-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 500;
  }
  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
    transition: background-color 150ms ease-in-out;
    cursor: pointer;
  }
  .dropdown-item:hover, .dropdown-item.selected {
    background-color: #F8FAFB;
  }

  .filter-select-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 500;
  }

  .v-enter-active, .v-leave-active {
    transition: opacity 150ms ease-in-out;
  }
  .v-enter-from, .v-enter, .v-leave-to {
    opacity: 0;
  }
  .v-enter-to, .v-leave-from {
    opacity: 1;
  }

  /* class is applied to elements we don't want users to be able to select text on */
  .no-select {
  -webkit-user-select: none; /* Safari */        
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
  }
</style>

<style scoped>
div.vc-container {
  border: 0px !important;
}
</style>
